import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
    Loader2,
    ShoppingCart,
    Package,
    ChevronRight,
    ChevronLeft,
    Copy,
    Filter,
    Sparkles,
    ClipboardList,
    FileText,
    CheckCircle2,
    XCircle,
    Clock,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ordersAPI } from '../services/api';
import { Order } from '../types';
import {
    Select,
    SelectTrigger,
    SelectContent,
    SelectItem,
    SelectValue
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import CreateOrderModal from '@/components/CreateOrderModal';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const faNumber = (n: number | string) =>
    new Intl.NumberFormat('fa-IR').format(typeof n === 'string' ? parseFloat(n) : n);

const formatFaDate = (d: string | number | Date) =>
    new Date(d).toLocaleDateString('fa-IR');

const statusClass = (status: string) => {
    switch (status) {
        case 'paid':
        case 'confirmed':
            return 'bg-green-600 text-white';
        case 'delivered':
            return 'bg-emerald-500 text-white';
        case 'cancelled':
            return 'bg-red-600 text-white';
        case 'pending_payment':
            return 'bg-amber-500 text-white';
        default:
            return 'bg-gray-200 text-gray-700';
    }
};

const statusLabel = (status: string) => {
    switch (status) {
        case 'paid':
            return 'پرداخت‌شده';
        case 'confirmed':
            return 'تأیید شده';
        case 'delivered':
            return 'تحویل شده';
        case 'cancelled':
            return 'لغو شده';
        case 'pending_payment':
            return 'در انتظار پرداخت';
        default:
            return status;
    }
};

type Filters = {
    status: string;
    q: string;
    from: string;
    to: string;
};

const Orders: React.FC = () => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // pagination state
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // فیلترها
    const [filters, setFilters] = useState<Filters>({
        status: 'all',
        q: '',
        from: '',
        to: ''
    });
    const [applied, setApplied] = useState<Filters>(filters);

    // جزئیات مودال
    const [selectedOrder, setSelectedOrder] = useState<any>(null);
    const [detailsOpen, setDetailsOpen] = useState(false);

    // باز/بسته بودن پنل فیلتر
    const [filtersOpen, setFiltersOpen] = useState(false);

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            const ordersData = await ordersAPI.getAllOrders();
            setOrders(ordersData);
        } catch (err: unknown) {
            const errorMessage =
                err instanceof Error && 'response' in err
                    ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
                    : 'خطا در دریافت سفارش‌ها';
            setError(errorMessage || 'خطا در دریافت سفارش‌ها');
        } finally {
            setLoading(false);
        }
    };

    // آمار کلی برای هدر
    const stats = useMemo(() => {
        const total = orders.length;
        const paidLike = orders.filter(
            (o: any) => ['paid', 'confirmed', 'delivered'].includes(o.status)
        ).length;
        const pending = orders.filter((o: any) => o.status === 'pending_payment').length;
        const cancelled = orders.filter((o: any) => o.status === 'cancelled').length;
        return { total, paidLike, pending, cancelled };
    }, [orders]);

    // ----- فیلترکردن داده‌ها -----
    const filteredOrders = useMemo(() => {
        const q = (applied.q || '').trim();
        const hasQ = q.length > 0;
        const from = applied.from ? new Date(applied.from) : null;
        const to = applied.to ? new Date(applied.to) : null;

        return orders.filter((o: any) => {
            if (applied.status !== 'all' && o.status !== applied.status) return false;
            if (hasQ) {
                const hay = [
                    String(o.id || ''),
                    String(o.product?.title || o.product?.name || ''),
                ].join(' ').toLowerCase();
                if (!hay.includes(q.toLowerCase())) return false;
            }
            if (from || to) {
                const cd = new Date(o.created_at);
                if (from && cd < from) return false;
                if (to) {
                    const toEnd = new Date(to);
                    toEnd.setHours(23, 59, 59, 999);
                    if (cd > toEnd) return false;
                }
            }
            return true;
        });
    }, [orders, applied]);

    // صفحه‌بندی بعد از فیلتر
    const totalItems = filteredOrders.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [totalPages, page]);

    const pagedOrders = useMemo(() => {
        const start = (page - 1) * pageSize;
        const end = start + pageSize;
        return filteredOrders.slice(start, end);
    }, [filteredOrders, page, pageSize]);

    const openDetails = (order: any) => {
        setSelectedOrder(order);
        setDetailsOpen(true);
    };

    const copyLicenses = () => {
        if (!selectedOrder?.licenses) return;
        const text = selectedOrder.licenses.map((l: any) => l.license_key).join('\n');
        navigator.clipboard.writeText(text);
    };

    const applyFilters = () => {
        setApplied(filters);
        setPage(1);
    };

    const clearFilters = () => {
        const reset: Filters = { status: 'all', q: '', from: '', to: '' };
        setFilters(reset);
        setApplied(reset);
        setPage(1);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
                <div className="text-center">
                    <Loader2 className="h-12 w-12 animate-spin text-green-600 mx-auto mb-4" />
                    <p className="text-green-700 font-medium">در حال بارگذاری سفارش‌ها...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-4" dir="rtl">
                <Alert variant="destructive" className="max-w-xl w-full">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div
            dir="rtl"
            className="space-y-6 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 min-h-screen p-4 md:p-8"
        >
            {/* هدر گرادیانی */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 p-6 md:p-8 shadow-2xl mb-2">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20"></div>
                <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div className="text-white space-y-2">
                        <div className="flex items-center gap-3">
                            <Sparkles className="h-8 w-8" />
                            <h1 className="text-3xl md:text-4xl font-bold">مدیریت سفارش‌ها</h1>
                        </div>
                        <p className="text-green-100 text-lg">
                            پیگیری وضعیت سفارش‌ها، مشاهده جزئیات و مدیریت لایسنس‌های صادر شده
                        </p>
                        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2 text-sm border border-white/30">
                                <div className="text-emerald-50 text-xs">کل سفارش‌ها</div>
                                <div className="text-lg font-bold">{faNumber(stats.total)}</div>
                            </div>
                            <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2 text-sm border border-white/30">
                                <div className="text-emerald-50 text-xs">پرداخت/تحویل شده</div>
                                <div className="text-lg font-bold text-emerald-100">{faNumber(stats.paidLike)}</div>
                            </div>
                            <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2 text-sm border border-white/30">
                                <div className="text-emerald-50 text-xs">در انتظار پرداخت</div>
                                <div className="text-lg font-bold text-amber-100">{faNumber(stats.pending)}</div>
                            </div>
                            <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2 text-sm border border-white/30">
                                <div className="text-emerald-50 text-xs">لغو شده</div>
                                <div className="text-lg font-bold text-red-100">{faNumber(stats.cancelled)}</div>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col gap-3 items-stretch md:items-end">
                        <div className="bg-white/15 backdrop-blur-md rounded-2xl px-4 py-3 border border-white/30 flex flex-col gap-2 min-w-[220px]">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-emerald-50 flex items-center gap-1">
                                    <ClipboardList className="h-4 w-4" />
                                    ایجاد سفارش جدید
                                </span>
                            </div>
                            <p className="text-xs text-emerald-100">
                                از اینجا می‌توانید برای محصولات جدید سفارش ثبت کنید.
                            </p>
                            <CreateOrderModal onCreated={fetchOrders} />
                        </div>
                    </div>
                </div>
            </div>

            {/* فیلتر */}
            <Card className="border-green-200/60 bg-white/80 backdrop-blur-sm shadow-lg">
                <CardHeader className="pb-3 border-b border-green-100">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-green-600 flex items-center justify-center">
                                <Filter className="h-5 w-5 text-white" />
                            </div>
                            <div>
                                <CardTitle className="text-green-900 text-base md:text-lg">فیلتر سفارش‌ها</CardTitle>
                                <CardDescription className="text-green-700">
                                    فیلتر بر اساس وضعیت، نام/شناسهٔ محصول و بازهٔ تاریخ
                                </CardDescription>
                            </div>
                        </div>
                        <Button
                            variant="outline"
                            className="border-green-300 text-green-700 hover:bg-green-50"
                            onClick={() => setFiltersOpen((v) => !v)}
                        >
                            {filtersOpen ? 'بستن فیلترها' : 'نمایش فیلترها'}
                        </Button>
                    </div>
                </CardHeader>
                {filtersOpen && (
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                            <div className="space-y-1 md:col-span-1">
                                <span className="text-sm text-gray-600">وضعیت</span>
                                <Select
                                    value={filters.status}
                                    onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}
                                >
                                    <SelectTrigger className="border-green-300 focus:ring-green-600 focus:ring-2">
                                        <SelectValue placeholder="همه" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">همه</SelectItem>
                                        <SelectItem value="pending_payment">در انتظار پرداخت</SelectItem>
                                        <SelectItem value="paid">پرداخت‌شده</SelectItem>
                                        <SelectItem value="confirmed">تأیید شده</SelectItem>
                                        <SelectItem value="delivered">تحویل شده</SelectItem>
                                        <SelectItem value="cancelled">لغو شده</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1 md:col-span-2">
                                <span className="text-sm text-gray-600">جست‌وجو (نام محصول/شناسه)</span>
                                <Input
                                    value={filters.q}
                                    onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                                    placeholder="مثلاً: amlak یا 1024"
                                    className="border-green-300 focus:ring-2 focus:ring-green-600"
                                />
                            </div>
                            <div className="space-y-1">
                                <span className="text-sm text-gray-600">از تاریخ</span>
                                <Input
                                    type="date"
                                    value={filters.from}
                                    onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
                                    className="border-green-300 focus:ring-2 focus:ring-green-600"
                                />
                            </div>
                            <div className="space-y-1">
                                <span className="text-sm text-gray-600">تا تاریخ</span>
                                <Input
                                    type="date"
                                    value={filters.to}
                                    onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
                                    className="border-green-300 focus:ring-2 focus:ring-green-600"
                                />
                            </div>
                        </div>
                        <div className="mt-3 flex flex-col sm:flex-row gap-2 justify-start sm:items-center">
                            <Button onClick={applyFilters} className="bg-green-600 hover:bg-green-700 text-white">
                                اعمال فیلتر
                            </Button>
                            <Button variant="outline" onClick={clearFilters} className="border-green-300 text-green-700 hover:bg-green-50">
                                حذف فیلترها
                            </Button>
                            <div className="text-sm text-gray-600 sm:mr-auto sm:text-left">
                                {faNumber(totalItems)} نتیجه پس از فیلتر
                            </div>
                        </div>
                    </CardContent>
                )}
            </Card>

            {orders.length === 0 ? (
                <Card className="border-emerald-600/60 bg-white/80 backdrop-blur-sm shadow-lg">
                    <CardContent className="text-center py-12">
                        <ShoppingCart className="h-12 w-12 mx-auto mb-4 text-green-400" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">سفارشی یافت نشد</h3>
                        <p className="text-gray-500 mb-4">با ایجاد اولین سفارش شروع کنید</p>
                        <CreateOrderModal onCreated={fetchOrders} />
                    </CardContent>
                </Card>
            ) : (
                <Card className="border-emerald-600/60 border-2 bg-white/80 backdrop-blur-sm shadow-lg">
                    <CardHeader className="text-right border-b border-green-100 pb-3">
                        <CardTitle className="text-gray-900 flex items-center gap-2">
                            <Package className="h-5 w-5 text-green-600" />
                            سوابق سفارش
                        </CardTitle>
                        <CardDescription className="text-green-700">نمایش و مدیریت همه‌ی سفارش‌ها</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4">
                        {/* موبایل: کارت‌ها */}
                        <div className="block md:hidden space-y-3">
                            {pagedOrders.map((order) => {
                                const title = (order as any).product?.title || (order as any).product?.name;
                                const invoiceId = (order as any).invoice?.id;
                                const isPendingPayment = order.status === 'pending_payment';

                                return (
                                    <div key={order.id} className="rounded-xl border border-emerald-600/60 bg-white p-4 shadow-sm">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Package className="h-5 w-5 text-green-600" />
                                                <div className="font-semibold text-gray-800">
                                                    {title || `محصول #${(order as any).product_id}`}
                                                </div>
                                            </div>
                                            <Badge className={statusClass((order as any).status)}>
                                                {statusLabel((order as any).status)}
                                            </Badge>
                                        </div>
                                        <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                                            <div className="flex flex-col">
                                                <span className="text-gray-500">شناسه</span>
                                                <span className="font-medium"> {order.reference_code || `#${order.id}`}</span>
                                            </div>
                                            <div className="flex flex-col items-end">
                                                <span className="text-gray-500">تاریخ</span>
                                                <span className="font-medium">{formatFaDate((order as any).created_at)}</span>
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-gray-500">تعداد</span>
                                                <span className="font-medium">{faNumber((order as any).quantity)}</span>
                                            </div>
                                            <div className="flex flex-col items-end">
                                                <span className="text-gray-500">مبلغ کل</span>
                                                <span className="font-semibold text-green-700">
                                                    {faNumber(((order as any).total_price / 10) ?? 0)} تومان
                                                </span>
                                            </div>
                                        </div>
                                        <div className="mt-4 flex flex-wrap gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => openDetails(order)}
                                                className="flex-1 border-green-400 text-green-700 hover:bg-green-50"
                                            >
                                                مشاهده جزئیات
                                            </Button>
                                            {invoiceId && (
                                                <Button
                                                    size="sm"
                                                    onClick={() => navigate(`/invoices/${invoiceId}`)}
                                                    className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                                                >
                                                    <FileText className="h-4 w-4 ml-1" />
                                                    مشاهده فاکتور
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* دسکتاپ: جدول */}
                        <div className="hidden md:block rounded-xl overflow-hidden border border-green-100 bg-white">
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-green-50">
                                            <TableHead className="text-right text-green-700 whitespace-nowrap">شناسه</TableHead>
                                            <TableHead className="text-right text-green-700 whitespace-nowrap">محصول</TableHead>
                                            <TableHead className="text-right text-green-700 whitespace-nowrap">تعداد</TableHead>
                                            <TableHead className="text-right text-green-700 whitespace-nowrap">مبلغ کل</TableHead>
                                            <TableHead className="text-right text-green-700 whitespace-nowrap">وضعیت</TableHead>
                                            <TableHead className="text-right text-green-700 whitespace-nowrap">تاریخ</TableHead>
                                            <TableHead className="text-right text-green-700 whitespace-nowrap">عملیات</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {pagedOrders.map((order) => {
                                            const invoiceId = (order as any).invoice?.id;
                                            const isPendingPayment = order.status === 'pending_payment';

                                            return (
                                                <TableRow key={order.id} className="hover:bg-green-50/40 border-emerald-600/60">
                                                    <TableCell className="font-medium text-right whitespace-nowrap"> {order.reference_code || `#${order.id}`}</TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center justify-end gap-2">
                                                            <span>{(order as any).product?.title || (order as any).product?.name}</span>
                                                            <Package className="h-4 w-4 text-green-500" />
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-right whitespace-nowrap">{faNumber((order as any).quantity)}</TableCell>
                                                    <TableCell className="font-medium text-right whitespace-nowrap">
                                                        {faNumber(((order as any).total_price / 10) ?? 0)} تومان
                                                    </TableCell>
                                                    <TableCell className="text-right whitespace-nowrap">
                                                        <Badge className={statusClass((order as any).status)}>
                                                            {statusLabel((order as any).status)}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="text-right whitespace-nowrap">
                                                        {formatFaDate((order as any).created_at)}
                                                    </TableCell>
                                                    <TableCell className="text-right whitespace-nowrap">
                                                        <div className="flex flex-wrap gap-2">
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => openDetails(order)}
                                                                className="border-green-400 text-green-700 hover:bg-green-50"
                                                            >
                                                                جزئیات
                                                            </Button>
                                                            {invoiceId && (
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => navigate(`/invoices/${invoiceId}`)}
                                                                    className="bg-amber-600 hover:bg-amber-700 text-white"
                                                                >
                                                                    <FileText className="h-4 w-4 ml-1" />
                                                                    فاکتور
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>

                        {/* کنترل‌های صفحه‌بندی */}
                        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">آیتم در هر صفحه:</span>
                                <Select
                                    value={String(pageSize)}
                                    onValueChange={(v) => {
                                        setPageSize(parseInt(v, 10));
                                        setPage(1);
                                    }}
                                >
                                    <SelectTrigger className="w-24 border-green-300 focus:ring-green-600 focus:ring-2">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="5">۵</SelectItem>
                                        <SelectItem value="10">۱۰</SelectItem>
                                        <SelectItem value="20">۲۰</SelectItem>
                                        <SelectItem value="50">۵۰</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="text-sm text-gray-600">
                                صفحه {faNumber(page)} از {faNumber(totalPages)} — {faNumber(totalItems)} سفارش
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page <= 1}
                                    className="border-green-400 text-green-700 hover:bg-green-50 disabled:opacity-50"
                                >
                                    <ChevronRight className="ml-1 h-4 w-4" /> قبلی
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={page >= totalPages}
                                    className="border-green-400 text-green-700 hover:bg-green-50 disabled:opacity-50"
                                >
                                    بعدی <ChevronLeft className="mr-1 h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* مودال جزئیات */}
            <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
                <DialogContent dir="rtl" className="sm:max-w-lg rounded-2xl p-6">
                    <DialogHeader className="text-right border-b pb-2 mb-3">
                        <DialogTitle className="text-xl font-semibold text-green-700">جزئیات سفارش #{selectedOrder?.id}</DialogTitle>
                    </DialogHeader>
                    {selectedOrder ? (
                        <div className="space-y-3 text-right text-sm">
                            <div className="flex justify-between border-b pb-1">
                                <span className="text-gray-500">تاریخ ثبت:</span>
                                <span>{formatFaDate(selectedOrder.created_at)}</span>
                            </div>
                            <div className="flex justify-between border-b pb-1">
                                <span className="text-gray-500">وضعیت:</span>
                                <Badge className={statusClass(selectedOrder.status)}>
                                    {statusLabel(selectedOrder.status)}
                                </Badge>
                            </div>
                            <div className="flex justify-between border-b pb-1">
                                <span className="text-gray-500">مبلغ کل:</span>
                                <span className="font-semibold text-green-600">
                                    {faNumber((selectedOrder.total_price / 10) ?? 0)} تومان
                                </span>
                            </div>
                            {selectedOrder.product && (
                                <div className="border rounded-lg p-3 bg-green-50/30">
                                    <div className="font-medium text-gray-800 mb-1">محصول</div>
                                    <div className="text-sm text-gray-600">
                                        <div>نام: {selectedOrder.product.title || selectedOrder.product.name}</div>
                                        <div>قیمت واحد: {faNumber(selectedOrder.product.price / 10)} تومان</div>
                                    </div>
                                </div>
                            )}
                            {selectedOrder.invoice && (
                                <div className="border rounded-lg p-3 bg-green-50/30">
                                    <div className="font-medium text-gray-800 mb-1">فاکتور</div>
                                    <div className="text-sm text-gray-600">
                                        <div>کد فاکتور: {selectedOrder.invoice.id}</div>
                                        <div>تعداد لایسنس: {faNumber(selectedOrder.invoice.licence_count)}</div>
                                        <div>وضعیت: {selectedOrder.invoice.status}</div>
                                    </div>
                                </div>
                            )}
                            {selectedOrder.licenses && (
                                <div className="border rounded-lg p-3 bg-green-50/30">
                                    <div className="flex justify-between items-center mb-2">
                                        <div className="font-medium text-gray-800">لایسنس‌ها ({selectedOrder.licenses.length})</div>
                                        <Button variant="outline" size="sm" onClick={copyLicenses} className="border-green-400 text-green-700 hover:bg-green-100">
                                            <Copy className="ml-2 h-4 w-4" /> کپی همه
                                        </Button>
                                    </div>
                                    <div dir="ltr" className="text-xs bg-white rounded-md border p-2 max-h-40 overflow-auto text-gray-700">
                                        {selectedOrder.licenses.map((l: any) => (
                                            <div key={l.id}>{l.license_key}</div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* دکمه مشاهده فاکتور به جای پرداخت */}
                            {selectedOrder.invoice?.id && (
                                <div className="pt-3 border-t border-amber-200">
                                    <Button
                                        onClick={() => {
                                            setDetailsOpen(false);
                                            navigate(`/invoices/${selectedOrder.invoice.id}`);
                                        }}
                                        className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                                    >
                                        <FileText className="h-4 w-4 ml-2" />
                                        مشاهده فاکتور و پرداخت
                                    </Button>
                                    <p className="text-xs text-center text-gray-500 mt-2">
                                        برای پرداخت، به صفحه فاکتور بروید.
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-500">در حال بارگذاری...</div>
                    )}
                    <DialogFooter className="mt-4 flex justify-end">
                        <Button onClick={() => setDetailsOpen(false)} className="bg-green-600 hover:bg-green-700 text-white rounded-xl px-6">
                            بستن
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Orders;
